import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChatIAButton } from '../../components/ChatIA';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  Fade,
  alpha,
  useTheme,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Tooltip,
  Button,
  Collapse,
  IconButton,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import QuadrimestreSelector, { getQuadrimestreInicial, saveQuadrimestre, formatQuadrimestreParam } from '../../components/saude/QuadrimestreSelector';
import FonteDadosSelector, { getFonteInicial, FonteDados } from '../../components/saude/FonteDadosSelector';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupsIcon from '@mui/icons-material/Groups';
import HelpButton from '../../components/saude/HelpButton';
import CircularScore from '../../components/saude/CircularScore';
import ComponenteQualidadeCard from '../../components/saude/ComponenteQualidadeCard';
// import EixoBarChart from '../../components/saude/EixoBarChart'; // bloco "Desempenho por Eixo" ocultado — reativar junto com o JSX comentado
import AlertaCard from '../../components/saude/AlertaCard';
import { getClassificacaoColor } from '../../components/saude/ClassificacaoBadge';
import TrendBadge from '../../components/saude/TrendBadge';
import EvolucaoChart from '../../components/saude/EvolucaoChart';
import CompletudeHeatmap from '../../components/saude/CompletudeHeatmap';
import { EIXO_GROUPS } from '../../data/eixo-groups';
import { buildDashboardContext, buildAnalysisContext } from '../../utils/saude-context-text';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';

/**
 * Texto do valor de um indicador: valor real atingido (% ou razao) quando disponivel
 * (eixos com faixa/banda: eSB, eMulti), senao o percentual.
 */
function formatIndicadorValor(ind: any): string {
  return (ind?.valor !== undefined && ind?.valor !== null)
    ? (ind.unidade === 'ratio'
        ? ind.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : `${ind.valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`)
    : `${ind.percentual}%`;
}

/** Compute top improvement opportunities across all eixos */
function computeOportunidades(eixos: any[]) {
  const oportunidades: { titulo: string; descricao: string; eixo: string; potencial: number; tipo: 'oportunidade' }[] = [];
  for (const e of eixos) {
    if (!e.indicadores?.length) continue;
    for (const ind of e.indicadores) {
      const potencial = ((100 - (ind.percentual ?? 0)) * (ind.peso ?? 0)) / 100;
      if (potencial > 2) {
        oportunidades.push({
          titulo: `${e.nome}: ${ind.nome || ind.indicador}`,
          descricao: `+${potencial.toFixed(1)} pts se melhorar de ${ind.percentual?.toFixed(0)}% para 100%`,
          eixo: e.eixo,
          potencial,
          tipo: 'oportunidade',
        });
      }
    }
  }
  return oportunidades.sort((a, b) => b.potencial - a.potencial).slice(0, 5);
}

/** Compute eixos that need attention */
function computeAlertas(eixos: any[]) {
  return eixos
    .filter((e: any) => {
      if (e.classificacao !== 'Regular' && e.classificacao !== 'Suficiente') return false;
      if (!e.total_identificados) return false;
      if (e.indicadores?.length && e.indicadores.every((ind: any) => !ind.numerador)) return false;
      return true;
    })
    .sort((a: any, b: any) => (a.score_medio ?? 0) - (b.score_medio ?? 0))
    .slice(0, 5)
    .map((e: any) => ({
      titulo: e.nome,
      descricao: `Score ${e.score_medio ?? 0} pts - ${e.classificacao}`,
      eixo: e.eixo,
      tipo: 'atencao' as const,
    }));
}

const EXPANDED_GROUPS_KEY = 'saude_expanded_groups';

// Sigla curta de cada grupo para compor o titulo "Indicadores (...)" no Hero
const GROUP_SIGLA: Record<string, string> = {
  'esf-eap': 'eSF',
  'esb': 'eSB',
  'emulti': 'eMulti',
  'eapp': 'eAPP',
  'ecr': 'eCR',
};

// Grupos considerados na soma e no label de "Indicadores" (Complementares fica de fora)
const GRUPOS_CONSIDERADOS = EIXO_GROUPS.filter(g => g.key !== 'complementares');
const EIXOS_CONSIDERADOS = new Set(GRUPOS_CONSIDERADOS.flatMap(g => g.eixos));

const SaudeDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  // Progressive loading state: data per eixo
  const [eixosData, setEixosData] = useState<Record<string, any>>({});
  const [eixosLoading, setEixosLoading] = useState<Set<string>>(new Set());
  const [eixosError, setEixosError] = useState<Set<string>>(new Set());
  const [initialError, setInitialError] = useState<string | null>(null);

  const qAtual = getQuadrimestreInicial();
  const [quadrimestre, setQuadrimestre] = useState(qAtual.quadrimestre);
  const [ano, setAno] = useState(qAtual.ano);
  const [fonte, setFonte] = useState<FonteDados>(getFonteInicial());

  const [evolucao, setEvolucao] = useState<any>(null);
  const [evolucaoLoading, setEvolucaoLoading] = useState(false);

  const [completude, setCompletude] = useState<any>(null);
  const [completudeExpanded, setCompletudeExpanded] = useState(false);
  const [completudeLoading, setCompletudeLoading] = useState(false);

  // Contagem de equipes por tipo (busca rapida, independe do quadrimestre)
  const [equipesTipos, setEquipesTipos] = useState<any>(null);

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const [reloading, setReloading] = useState(false);
  const [reloadMsg, setReloadMsg] = useState<string | null>(null);
  // Incrementado no "Recarregar Dados" para forcar refetch do card de Componente Qualidade
  const [reloadToken, setReloadToken] = useState(0);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const saved = sessionStorage.getItem(EXPANDED_GROUPS_KEY);
      if (saved) {
        const keys: string[] = JSON.parse(saved);
        const valid = EIXO_GROUPS.map((g) => g.key);
        return new Set(keys.filter((k) => valid.includes(k)));
      }
    } catch { /* storage indisponivel — usa default */ }
    return new Set(EIXO_GROUPS.filter((g) => g.defaultExpanded).map((g) => g.key));
  });

  // Persiste o estado aberto/fechado dos paineis para restaurar ao voltar para /saude
  useEffect(() => {
    try {
      sessionStorage.setItem(EXPANDED_GROUPS_KEY, JSON.stringify(Array.from(expandedGroups)));
    } catch { /* ignora falha de storage */ }
  }, [expandedGroups]);

  // Track which eixos have been requested (to avoid duplicate fetches)
  const requestedEixos = useRef<Set<string>>(new Set());
  // Track current quadrimestre param to detect changes
  const currentQParam = useRef<string>('');

  const handleQuadChange = (q: string, a: number) => { setQuadrimestre(q); setAno(a); saveQuadrimestre(q, a); };

  const loadEixos = useCallback((eixos: string[], qParam: string) => {
    for (const eixo of eixos) {
      if (requestedEixos.current.has(eixo)) continue;
      requestedEixos.current.add(eixo);
      setEixosLoading(prev => new Set(prev).add(eixo));
      saudeService.getEixoResumo(eixo, municipioAtivo!.id, undefined, qParam, unidadeSelecionada?.codunidade)
        .then(data => {
          setEixosData(prev => ({ ...prev, [eixo]: data }));
        })
        .catch(() => {
          setEixosError(prev => new Set(prev).add(eixo));
        })
        .finally(() => {
          setEixosLoading(prev => { const n = new Set(prev); n.delete(eixo); return n; });
        });
    }
  }, [municipioAtivo, unidadeSelecionada?.codunidade]);

  // Load default group eixos when municipio/quadrimestre changes
  useEffect(() => {
    if (!municipioAtivo) return;
    const qParam = formatQuadrimestreParam(quadrimestre, ano);
    const fetchKey = `${qParam}::${unidadeSelecionada?.codunidade || ''}::${fonte}`;

    // Reset state on quadrimestre/unidade change
    if (currentQParam.current !== fetchKey) {
      currentQParam.current = fetchKey;
      requestedEixos.current = new Set();
      setEixosData({});
      setEixosLoading(new Set());
      setEixosError(new Set());
      setInitialError(null);
      setEvolucao(null);
      setCompletude(null);
    }

    // Load eixos from all currently expanded groups
    const eixosIniciais = EIXO_GROUPS
      .filter(g => expandedGroups.has(g.key))
      .flatMap(g => g.eixos);

    loadEixos(eixosIniciais, qParam);
  }, [municipioAtivo, quadrimestre, ano, fonte, loadEixos, expandedGroups, unidadeSelecionada?.codunidade]);

  const handleAccordionChange = (groupKey: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (isExpanded) next.add(groupKey); else next.delete(groupKey);
      return next;
    });
    if (isExpanded && municipioAtivo) {
      const group = EIXO_GROUPS.find(g => g.key === groupKey);
      if (group) {
        const qParam = formatQuadrimestreParam(quadrimestre, ano);
        loadEixos(group.eixos, qParam);
      }
    }
  };

  const handleExportPdf = async () => {
    if (!municipioAtivo) return;
    try {
      setPdfLoading(true);
      setPdfError(null);
      const qParam = formatQuadrimestreParam(quadrimestre, ano);
      await saudeService.exportarPdf(municipioAtivo.id, qParam, unidadeSelecionada?.codunidade);
    } catch (err: any) {
      setPdfError(err?.response?.data?.message || 'Erro ao gerar o relatorio PDF.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleReload = async () => {
    if (!municipioAtivo || reloading) return;
    try {
      setReloading(true);
      setReloadMsg(null);
      // Limpa o cache Redis do municipio
      await saudeService.reloadDados(municipioAtivo.id);
      // Forca o card de Componente Qualidade a refazer o fetch (cache ja invalidado acima)
      setReloadToken(t => t + 1);
      // Forca refetch fresco dos eixos dos grupos expandidos (agora MISS -> query nova -> recacheia)
      requestedEixos.current = new Set();
      setEixosData({});
      setEixosLoading(new Set());
      setEixosError(new Set());
      setEvolucao(null);
      setCompletude(null);
      const qParam = formatQuadrimestreParam(quadrimestre, ano);
      const eixosIniciais = EIXO_GROUPS
        .filter(g => expandedGroups.has(g.key))
        .flatMap(g => g.eixos);
      loadEixos(eixosIniciais, qParam);
      setReloadMsg('Dados recarregados com sucesso.');
    } catch (err: any) {
      console.error('Erro ao recarregar dados do /saude:', err);
      setReloadMsg(err?.response?.data?.message || 'Nao foi possivel recarregar os dados.');
    } finally {
      setReloading(false);
    }
  };

  const handleLoadEvolucao = () => {
    if (!municipioAtivo || evolucaoLoading) return;
    setEvolucaoLoading(true);
    saudeService.getEvolucaoTemporal(municipioAtivo.id, unidadeSelecionada?.codunidade)
      .then(setEvolucao)
      .catch(() => {})
      .finally(() => setEvolucaoLoading(false));
  };

  // Lazy load completude only when expanded
  useEffect(() => {
    if (!completudeExpanded || !municipioAtivo?.id || completude) return;
    setCompletudeLoading(true);
    const qParam = formatQuadrimestreParam(quadrimestre, ano);
    saudeService.getCompletude(municipioAtivo.id, qParam, unidadeSelecionada?.codunidade)
      .then(setCompletude)
      .catch(() => { setCompletude(null); })
      .finally(() => { setCompletudeLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completudeExpanded, municipioAtivo?.id, quadrimestre, ano, unidadeSelecionada?.codunidade]);

  // Contagem de equipes por tipo — 1 chamada rapida ao trocar municipio/unidade
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    setEquipesTipos(null);
    saudeService.getEquipesTipos(municipioAtivo.id, unidadeSelecionada?.codunidade)
      .then(setEquipesTipos)
      .catch(() => setEquipesTipos(null));
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade]);

  // Derived values from progressively loaded eixos
  const eixos = useMemo(() => Object.values(eixosData), [eixosData]);
  const scoreGeral = useMemo(() =>
    eixos.length > 0 ? Math.round(eixos.reduce((s, e) => s + (e.score_medio ?? 0), 0) / eixos.length) : 0
  , [eixos]);
  const anyLoading = eixosLoading.size > 0;

  const alertas = useMemo(() => computeAlertas(eixos), [eixos]);
  const oportunidades = useMemo(() => computeOportunidades(eixos), [eixos]);

  // Build resumo-like object for context builders
  const resumoLike = useMemo(() => {
    if (eixos.length === 0) return undefined;
    return {
      municipio_id: municipioAtivo?.id,
      eixos,
      score_geral: scoreGeral,
      classificacao_geral: scoreGeral >= 75 ? 'Otimo' : scoreGeral >= 50 ? 'Bom' : scoreGeral >= 25 ? 'Suficiente' : 'Regular',
    };
  }, [eixos, scoreGeral, municipioAtivo?.id]);

  const dadosContexto = useMemo(() => {
    if (!resumoLike) return undefined;
    return buildDashboardContext(resumoLike, evolucao, completude);
  }, [resumoLike, evolucao, completude]);
  const analysisContexto = useMemo(() => {
    if (!resumoLike) return undefined;
    return buildAnalysisContext(resumoLike, evolucao, completude);
  }, [resumoLike, evolucao, completude]);

  const countByClass = useMemo(() => {
    const counts = { Otimo: 0, Bom: 0, Suficiente: 0, Regular: 0 } as Record<string, number>;
    Object.entries(eixosData).forEach(([slug, e]: [string, any]) => {
      if (!EIXOS_CONSIDERADOS.has(slug)) return; // ignora Complementares na soma
      const c = e.classificacao || 'Regular';
      counts[c] = (counts[c] || 0) + 1;
    });
    return counts;
  }, [eixosData]);

  // Origem das contagens do bloco "Indicadores": grupos que ja tem dados carregados
  // (as contagens vem de eixosData e acumulam; refletir os grupos carregados mantem coerencia)
  const indicadoresFonte = useMemo(() => {
    const grupos = GRUPOS_CONSIDERADOS.filter(g => g.eixos.some(slug => eixosData[slug]));
    if (grupos.length === 0) return '';
    if (grupos.length === GRUPOS_CONSIDERADOS.length) return 'Geral';
    return grupos.map(g => GROUP_SIGLA[g.key] || g.label).join('; ');
  }, [eixosData]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>Nenhum municipio selecionado</Typography>
        <Typography variant="body1" color="text.secondary">Selecione um municipio na barra lateral para visualizar os indicadores de saude.</Typography>
      </Box>
    );
  }

  if (initialError) {
    return (<Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}><Alert severity="error" sx={{ borderRadius: 2 }}>{initialError}</Alert></Box>);
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Fade in timeout={600}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>Saude APS</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>{municipioAtivo.nome}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Tooltip title="Limpa o cache e recarrega os indicadores do municipio">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={reloading ? <CircularProgress size={16} /> : <RefreshIcon />}
                  onClick={handleReload}
                  disabled={reloading}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  {reloading ? 'Recarregando...' : 'Recarregar Dados'}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AccountBalanceIcon />}
              onClick={() => navigate('/saude/painel-ms')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Painel MS
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EmojiEventsIcon />}
              onClick={() => navigate('/saude/ranking')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Ranking
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={pdfLoading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
              onClick={handleExportPdf}
              disabled={pdfLoading}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              {pdfLoading ? 'Gerando...' : 'Exportar PDF'}
            </Button>
            {municipioAtivo && (
              <FonteDadosSelector municipioId={municipioAtivo.id} fonte={fonte} onChange={setFonte} quadrimestre={formatQuadrimestreParam(quadrimestre, ano)} />
            )}
            <QuadrimestreSelector quadrimestre={quadrimestre} ano={ano} onChange={handleQuadChange} />
          </Box>
        </Box>
      </Fade>

      {pdfError && (
        <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }} onClose={() => setPdfError(null)}>
          {pdfError}
        </Alert>
      )}

      {reloadMsg && (
        <Alert
          severity={reloadMsg.startsWith('Dados') ? 'success' : 'error'}
          sx={{ borderRadius: 2, mb: 2 }}
          onClose={() => setReloadMsg(null)}
        >
          {reloadMsg}
        </Alert>
      )}

      {/* Hero Section - renders immediately with progressive score */}
      <Fade in timeout={700}>
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3, overflow: 'visible' }}>
          <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'stretch', py: 3 }}>
            {/* Componente Qualidade - 40% */}
            <Box sx={{ display: 'flex', flexGrow: { xs: 1, md: 4 }, flexBasis: { xs: '100%', md: 0 }, minWidth: { md: 260 } }}>
              <ComponenteQualidadeCard
                municipioId={municipioAtivo.id}
                municipioNome={municipioAtivo.nome}
                quadrimestre={formatQuadrimestreParam(quadrimestre, ano)}
                unidade={unidadeSelecionada?.codunidade}
                fonte={fonte}
                reloadToken={reloadToken}
              />
            </Box>

            {/* Indicadores - 30% */}
            <Box sx={{ flexGrow: { xs: 1, md: 3 }, flexBasis: { xs: '100%', md: 0 }, minWidth: { md: 200 }, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, p: 1.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <AssessmentIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Indicadores{indicadoresFonte ? ` (${indicadoresFonte})` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(countByClass).map(([cls, count]) => (
                  <Chip
                    key={cls}
                    label={`${count} ${cls}`}
                    size="small"
                    sx={{
                      bgcolor: alpha(getClassificacaoColor(cls), 0.1),
                      color: getClassificacaoColor(cls),
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Total de Equipes - 30% */}
            <Box sx={{ flexGrow: { xs: 1, md: 3 }, flexBasis: { xs: '100%', md: 0 }, minWidth: { md: 200 }, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, p: 1.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <GroupsIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                <Typography variant="subtitle2" fontWeight={700}>Total de Equipes</Typography>
              </Box>
              {equipesTipos ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[
                    ['eSF/eAP', equipesTipos.eSF],
                    ['eSB', equipesTipos.eSB],
                    ['eMulti', equipesTipos.eMulti],
                    ['eAPP', equipesTipos.eAPP],
                    ['eCR', equipesTipos.eCR],
                  ].map(([label, valor]) => (
                    <Chip
                      key={String(label)}
                      label={`${valor} ${label}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">carregando...</Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {/* Bloco "Desempenho por Eixo" (EixoBarChart) — ocultado a pedido; mantido comentado para reativacao futura.
          Componente puramente apresentacional: nao dispara nenhuma chamada propria (consome o array `eixos` ja carregado),
          portanto comenta-lo nao remove nenhuma requisicao nem impacta o carregamento dos demais blocos. */}
      {/*
      {eixos.length > 0 && (
        <Fade in timeout={800}>
          <Box sx={{ mb: 3 }}>
            <EixoBarChart eixos={eixos} onEixoClick={(eixo) => navigate(`/saude/${eixo}`)} />
          </Box>
        </Fade>
      )}
      */}

      {/* Section title - "Indicadores APS" (antigo "Eixos por Tipo de Equipe") */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Indicadores APS</Typography>
      </Box>

      {/* Accordion groups */}
      {EIXO_GROUPS.map((group, groupIdx) => {
        const GroupIcon = group.icon;
        // Build aggregate from available eixos data for this group
        const groupEixosData = group.eixos.map(slug => eixosData[slug]).filter(Boolean);
        const groupEixosLoading = group.eixos.some(slug => eixosLoading.has(slug));
        const avgScore = groupEixosData.length > 0
          ? Math.round(groupEixosData.reduce((sum: number, e: any) => sum + (e.score_medio ?? 0), 0) / groupEixosData.length)
          : 0;
        const classificacaoCounts: Record<string, number> = {};
        groupEixosData.forEach((e: any) => {
          const c = e.classificacao || 'Regular';
          classificacaoCounts[c] = (classificacaoCounts[c] || 0) + 1;
        });
        const overallClassificacao = avgScore >= 75 ? 'Otimo' : avgScore >= 50 ? 'Bom' : avgScore >= 25 ? 'Suficiente' : 'Regular';

        return (
          <Fade in timeout={1000 + groupIdx * 100} key={group.key}>
            <Accordion
              expanded={expandedGroups.has(group.key)}
              onChange={handleAccordionChange(group.key)}
              disableGutters
              sx={{
                borderRadius: '12px !important',
                border: `1px solid ${theme.palette.divider}`,
                mb: 1.5, overflow: 'hidden',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: '0 0 12px 0 !important' },
                boxShadow: 'none',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ borderLeft: `4px solid ${group.color}`, '& .MuiAccordionSummary-content': { display: 'flex', alignItems: 'center', gap: 1.5, my: 1 } }}
              >
                <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha(group.color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', color: group.color, flexShrink: 0 }}>
                  <GroupIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={600} noWrap>
                    {group.label}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {groupEixosData.length}/{group.eixos.length} eixos
                      {groupEixosLoading && ' (carregando...)'}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', sm: 'block' } }}>
                    {group.descricao}
                  </Typography>
                </Box>
                {/* eSB e eMulti nao possuem totalizacao geral (cada indicador e um eixo proprio) */}
                {!['esb', 'emulti'].includes(group.key) && (
                  groupEixosData.length > 0 ? (
                    <CircularScore value={avgScore} color={getClassificacaoColor(overallClassificacao)} size={42} />
                  ) : groupEixosLoading ? (
                    <Skeleton variant="circular" width={42} height={42} />
                  ) : null
                )}
                {!['esb', 'emulti'].includes(group.key) && (
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
                    {Object.entries(classificacaoCounts).map(([cls, count]) => (
                      <Chip key={cls} label={`${count} ${cls}`} size="small"
                        sx={{ height: 22, fontSize: '0.65rem', bgcolor: alpha(getClassificacaoColor(cls), 0.1), color: getClassificacaoColor(cls), fontWeight: 600 }} />
                    ))}
                  </Box>
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {group.eixos.map((eixoSlug) => {
                  const eixoData = eixosData[eixoSlug];
                  const isLoading = eixosLoading.has(eixoSlug);
                  const hasError = eixosError.has(eixoSlug);

                  if (isLoading) {
                    return (
                      <Box key={eixoSlug} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
                        <Skeleton variant="text" width={180} height={24} />
                        <Skeleton variant="rectangular" sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                        <Skeleton variant="rectangular" width={72} height={22} sx={{ borderRadius: 2 }} />
                      </Box>
                    );
                  }

                  if (hasError) {
                    return (
                      <Box key={eixoSlug} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                        <Typography variant="body2" color="error">{eixoSlug}: erro ao carregar</Typography>
                      </Box>
                    );
                  }

                  if (!eixoData) return null;

                  const eixoColor = getClassificacaoColor(eixoData.classificacao);
                  const singleEixoGroup = group.eixos.length === 1 && eixoData.indicadores?.length > 1;
                  // Eixos de indicador unico com faixa (eSB B1-B6, eMulti M1/M2): mostrar o valor
                  // atingido (ex.: 76,9%) em vez do valor da faixa (score_medio = 100 no "Otimo").
                  // A barra segue cheia (dirigida por score_medio).
                  const indUnicoBandado = eixoData.indicadores?.length === 1 && eixoData.indicadores[0]?.valor != null;
                  const numeroEixo = indUnicoBandado ? formatIndicadorValor(eixoData.indicadores[0]) : (eixoData.score_medio ?? 0);
                  return (
                    <Box key={eixoData.eixo}>
                      <Box onClick={() => navigate(`/saude/${eixoData.eixo}`)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, px: 2, py: 1,
                          cursor: 'pointer', borderLeft: `4px solid ${eixoColor}`, borderTop: `1px solid ${theme.palette.divider}`,
                          transition: 'background-color 0.15s ease', '&:hover': { bgcolor: alpha(eixoColor, 0.04) },
                        }}
                      >
                        <Box sx={{ flex: { xs: 1, sm: '0 0 220px' }, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{eixoData.nome}</Typography>
                          <HelpButton eixo={eixoData.eixo} size={14} />
                        </Box>
                        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={Math.min(eixoData.score_medio ?? 0, 100)}
                            sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: alpha(eixoColor, 0.08), '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: eixoColor } }} />
                          <Typography variant="body2" fontWeight={700} sx={{ minWidth: 32, textAlign: 'right', color: eixoColor }}>
                            {numeroEixo}
                          </Typography>
                          {evolucao?.tendencias?.[eixoData.eixo] && (
                            <TrendBadge trend={evolucao.tendencias[eixoData.eixo].trend} delta={evolucao.tendencias[eixoData.eixo].delta} />
                          )}
                        </Box>
                        <Typography variant="body2" fontWeight={700} sx={{ display: { xs: 'block', sm: 'none' }, color: eixoColor }}>
                          {numeroEixo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                          {(eixoData.total_identificados ?? 0).toLocaleString('pt-BR')} id.
                        </Typography>
                        <Chip label={eixoData.classificacao} size="small"
                          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: alpha(eixoColor, 0.1), color: eixoColor, minWidth: 72 }} />
                        <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 20, flexShrink: 0 }} />
                      </Box>
                      {/* Show indicators inline for single-eixo groups (e.g. eSB, eMulti) */}
                      {singleEixoGroup && (
                        <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${theme.palette.divider}`, bgcolor: alpha(group.color, 0.02) }}>
                          <Grid container spacing={1}>
                            {eixoData.indicadores.map((ind: any) => {
                              const indColor = ind.classificacao
                                ? getClassificacaoColor(ind.classificacao)
                                : ind.percentual >= 75 ? '#2e7d32' : ind.percentual >= 50 ? '#1565c0' : ind.percentual >= 25 ? '#e65100' : '#c62828';
                              const valorTexto = formatIndicadorValor(ind);
                              const shortLabel = ind.nome?.split(' - ').pop()?.split('(')[0]?.trim() || ind.nome || '';
                              return (
                                <Grid item xs={12} sm={6} key={ind.indicador}>
                                  <Tooltip title={`${ind.indicador} - ${ind.nome}`} arrow placement="top">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                                      <Box sx={{
                                        width: 22, height: 22, borderRadius: '6px', flexShrink: 0,
                                        bgcolor: alpha(indColor, 0.12), color: indColor,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 700, fontSize: '0.7rem', lineHeight: 1,
                                      }}>
                                        {ind.indicador}
                                      </Box>
                                      <Typography variant="caption" sx={{ flexShrink: 0, maxWidth: 140, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {shortLabel}
                                      </Typography>
                                      <LinearProgress variant="determinate" value={Math.min(ind.percentual ?? 0, 100)}
                                        sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: alpha(indColor, 0.1), '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: indColor } }} />
                                      <Typography variant="caption" fontWeight={700} sx={{ minWidth: 36, textAlign: 'right', color: indColor }}>
                                        {valorTexto}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          </Fade>
        );
      })}

      {/* Evolucao Temporal - lazy loaded on demand */}
      {evolucao?.periodos ? (
        <Fade in timeout={850}>
          <Box sx={{ mb: 3 }}>
            <EvolucaoChart periodos={evolucao.periodos} />
          </Box>
        </Fade>
      ) : (
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={evolucaoLoading ? <CircularProgress size={16} /> : undefined}
              onClick={handleLoadEvolucao}
              disabled={evolucaoLoading}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              {evolucaoLoading ? 'Carregando...' : 'Carregar Evolucao Temporal'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Alertas + Oportunidades */}
      {(alertas.length > 0 || oportunidades.length > 0) && (
        <Fade in timeout={900}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <AlertaCard
                titulo="Pontos de Atencao"
                icon={<WarningAmberIcon sx={{ color: '#e65100' }} />}
                items={alertas}
                onItemClick={(eixo) => navigate(`/saude/${eixo}`)}
                emptyText="Nenhum eixo em situacao critica."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <AlertaCard
                titulo="Oportunidades de Melhoria"
                icon={<LightbulbIcon sx={{ color: '#1565c0' }} />}
                items={oportunidades}
                onItemClick={(eixo) => navigate(`/saude/${eixo}`)}
                emptyText="Todos os indicadores em bom desempenho."
              />
            </Grid>
          </Grid>
        </Fade>
      )}

      {/* Analise Estrategica IA - manual trigger (movido para o final da pagina) */}
      {municipioAtivo && analysisContexto && (
        <AnalisePredicaoCard
          dadosContexto={analysisContexto}
          municipioId={municipioAtivo.id}
          quadrimestre={quadrimestre}
          ano={ano}
          manualTrigger
        />
      )}

      {/* Completude de Dados - lazy loaded on expand (movido para o final da pagina) */}
      <Fade in timeout={875}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            mb: 3,
            overflow: 'hidden',
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer',
              py: 2,
              '&:last-child': { pb: 2 },
            }}
            onClick={() => setCompletudeExpanded(!completudeExpanded)}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha('#1565c0', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#1565c0',
                flexShrink: 0,
              }}
            >
              <FactCheckIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Completude de Dados
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Analise de indicadores sem dados registrados por equipe (eSF/eAP)
              </Typography>
            </Box>
            {completudeLoading ? (
              <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 2 }} />
            ) : completude && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {completude.alertas?.length > 0 && (
                  <Chip
                    label={`${completude.alertas.length} alertas`}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      bgcolor: alpha('#c62828', 0.1),
                      color: '#c62828',
                    }}
                  />
                )}
                <Chip
                  label={`${completude.eixos?.length || 0} eixos`}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                />
              </Box>
            )}
            <IconButton size="small" sx={{ transform: completudeExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
              <ExpandMoreIcon />
            </IconButton>
          </CardContent>
          <Collapse in={completudeExpanded}>
            <Box sx={{ px: 2, pb: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              {completudeLoading ? (
                <Box sx={{ py: 3 }}>
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                </Box>
              ) : completude ? (
                <>
                  {completude.alertas?.filter((a: any) => a.tipo === 'multiplos_eixos_zerados').map((alerta: any, idx: number) => (
                    <Alert
                      key={`alert-${idx}`}
                      severity="warning"
                      sx={{ mt: 1.5, borderRadius: 2, fontSize: '0.8rem' }}
                    >
                      {alerta.mensagem}
                    </Alert>
                  ))}
                  <Box sx={{ mt: 2 }}>
                    <CompletudeHeatmap data={completude} />
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Clique para carregar os dados de completude.
                </Typography>
              )}
            </Box>
          </Collapse>
        </Card>
      </Fade>

      {eixos.length === 0 && !anyLoading && (
        <Box sx={{ textAlign: 'center', py: 8, px: 3, backgroundColor: alpha(theme.palette.primary.main, 0.05), borderRadius: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>Nenhum eixo de indicador disponivel</Typography>
          <Typography variant="body2" color="text.secondary">Os dados de saude ainda nao foram carregados para este municipio.</Typography>
        </Box>
      )}
      <ChatIAButton contexto="saude" dadosContexto={dadosContexto} fabSx={{ bottom: 24, right: 24, top: 'auto' }} />
    </Box>
  );
};

export default SaudeDashboard;
